// Crop/composite tools for region-select local editing.
// Cross-origin images load through a same-origin proxy to avoid canvas tainting (the upstream
// image host sends no CORS headers).
// Fix: same-origin URLs (/api/img/… composite results) must NOT go through the proxy —
// a worker subrequest to our own Workers route gets an instant 522 from the edge (same failure
// mode as an earlier incident), which made the second edit always fail to load the composite.
// Same-origin images never taint the canvas anyway.

import { proxyImgUrl } from '../api';

export interface RegionRect { x: number; y: number; w: number; h: number }

function needsProxy(src: string): boolean {
  if (!src.startsWith('http')) return false; // dataURL / relative path loads directly
  try {
    return new URL(src).origin !== location.origin;
  } catch {
    return false;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image failed to load'));
    img.src = needsProxy(src) ? proxyImgUrl(src) : src;
  });
}

/** Crop a region in image pixel coordinates → PNG dataURL */
/** Crop a region out of the image. outScale > 1 enlarges the output (SPEC-379): the edit model rejects
 * inputs below its minimum resolution, so undersized regions are upscaled here and scaled back on composite. */
export async function cropRegion(src: string, rect: RegionRect, outScale = 1): Promise<string> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(rect.w * outScale));
  canvas.height = Math.max(1, Math.round(rect.h * outScale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

/** Paste the edited region back onto the original → new composite PNG dataURL */
export async function compositeRegion(origSrc: string, editedSrc: string, rect: RegionRect): Promise<string> {
  const [orig, edited] = await Promise.all([loadImage(origSrc), loadImage(editedSrc)]);
  const canvas = document.createElement('canvas');
  canvas.width = orig.naturalWidth;
  canvas.height = orig.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unavailable');
  ctx.drawImage(orig, 0, 0);
  ctx.drawImage(edited, rect.x, rect.y, rect.w, rect.h);
  return canvas.toDataURL('image/png');
}

/** Get an image's natural size (for canvas-coord ↔ pixel-coord conversion) */
export async function naturalSize(src: string): Promise<{ w: number; h: number }> {
  const img = await loadImage(src);
  return { w: img.naturalWidth, h: img.naturalHeight };
}
